/**
 * The Investor Portals view's rules (#11635 Round 4, documents-ui spec R17 to
 * R20; #15170): which services a client's pages list, the coverage rows of
 * the overview's Portals tab, and how the portal documents group, by manager
 * on the overview and by day on a portal's page. Pure functions over the
 * services read and the search rows, so every rule is unit-tested against a
 * fixed clock and the components only draw.
 */
import type {
  PortalNewestDocument,
  PortalService,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { formatRelativeTemporal } from "@/components/FpFundBrain/Manage/Connections/relativeTime";
import type { Temporal } from "@/utils/temporal";
import {
  calendarDaysBetween,
  formatInstant,
  formatInstantTime,
  previousCalendarDay,
  todayInZone,
} from "@/utils/temporal";
import type { DocumentRow } from "./api";

/** The uploader key a service's documents carry, from the service's name
 *  (spec R11 / R22; Round 7, R22: a service's page is scoped by it). */
export const serviceKeyOf = (name: string): string => `system:${name}`;

/** The uploader key a service's documents carry (spec R11 / R22). */
export const serviceUploaderKey = (service: PortalService): string =>
  serviceKeyOf(service.service);

/** The column's order for the portals under the Investor Portals row: by
 *  name (#15170). A client's page says nothing about a portal's state, and
 *  an order by state would say it. */
export const sortServicesForColumn = (
  services: PortalService[]
): PortalService[] =>
  [...services].sort((a, b) =>
    a.service.localeCompare(b.service, undefined, { sensitivity: "base" })
  );

const plural = (count: number, noun: string): string =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** A wire temporal as "2h ago" (instant) or the day itself (date); a dash
 *  for none. The overview's Last sync cell and the status texts share it. */
export const relativeOf = (value: Temporal | null): string =>
  value === null ? "—" : formatRelativeTemporal(value);

// --- the documents half --------------------------------------------------

/** A portal document's day row (#15170, match-mockup spec section 2). */
export interface RowGroup {
  key: `day:${ArrivalBucket}`;
  label: string;
}

/** The day a portal document groups under on the portal pages (#15170,
 *  match-mockup spec section 2): its arrival's calendar day in the viewer's
 *  zone against today's there, as `arrivalBucketOf` reads it. */
export const dayGroupOf = (
  row: DocumentRow,
  now: Date,
  zone: string
): RowGroup => {
  const bucket = arrivalBucketOf(row.uploadedAt, zone, now);
  return { key: `day:${bucket}`, label: ARRIVAL_LABEL[bucket] };
};

/** The rows reordered so each day's rows sit together, the days in the
 *  order their newest row appears; rows keep their own order within a day.
 *  The stream is newest-first already, so first appearance is newest. */
export const groupRows = (
  rows: DocumentRow[],
  of: (row: DocumentRow) => RowGroup
): DocumentRow[] => {
  const order: string[] = [];
  const byGroup = new Map<string, DocumentRow[]>();
  for (const row of rows) {
    const group = of(row);
    const bucket = byGroup.get(group.key);
    if (bucket) bucket.push(row);
    else {
      byGroup.set(group.key, [row]);
      order.push(group.key);
    }
  }
  return order.flatMap((key) => byGroup.get(key) ?? []);
};

// --- #15170: the client's Investor Portals page ----------------------------

/** The services a client's pages list (#15170): the client-healthy ones.
 *  A service that is not is simply absent; nothing says why. */
export const clientServices = (services: PortalService[]): PortalService[] =>
  services.filter((service) => service.client_healthy);

/** A manager a portal delivers for, as the pages link it. */
export interface CoverageManager {
  name: string;
  publicId: string;
}

/** One manager and portal pair on the Portals tab, By manager (#15170,
 *  match-mockup spec section 1): a flat row, never under a group heading. */
export interface CoverageRow {
  key: string;
  service: PortalService;
  /** The manager the portal delivers for; null for a portal matched to none
   *  yet, whose one row sits at the end. */
  manager: CoverageManager | null;
  /** The pair's documents in the last 7 days; for a portal matched to no
   *  manager, the portal's own. */
  weekCount: number;
  /** The newest of them the viewer may read, from the same read as the
   *  count; null when none. */
  newest: PortalNewestDocument | null;
}

const byName = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** The By manager rows (#15170, match-mockup spec section 1): one per
 *  portal and delivered manager, sorted by manager then portal; a portal
 *  with no delivered manager is one row with the manager blank, after every
 *  matched row. */
export const coverageRows = (services: PortalService[]): CoverageRow[] =>
  services
    .flatMap((service): CoverageRow[] =>
      service.delivered_managers.length > 0
        ? service.delivered_managers.map((manager) => ({
            key: `${service.connection_id}:${manager.public_id}`,
            service,
            manager: { name: manager.name, publicId: manager.public_id },
            weekCount: manager.recent_document_count,
            newest: manager.newest_recent_document,
          }))
        : [
            {
              key: `${service.connection_id}:none`,
              service,
              manager: null,
              weekCount: service.recent_document_count,
              newest: service.newest_recent_document,
            },
          ]
    )
    .sort((a, b) => {
      if (a.manager === null || b.manager === null) {
        if (a.manager !== null) return -1;
        if (b.manager !== null) return 1;
        return byName(a.service.service, b.service.service);
      }
      return (
        byName(a.manager.name, b.manager.name) ||
        byName(a.service.service, b.service.service)
      );
    });

/** One portal on the Portals tab, By portal (#15170, match-mockup spec
 *  section 1). */
export interface PortalRow {
  key: string;
  service: PortalService;
  /** Its delivered managers, A to Z, each a link to its page. */
  managers: CoverageManager[];
  weekCount: number;
  newest: PortalNewestDocument | null;
}

/** The By portal rows: one per portal, A to Z. */
export const portalRows = (services: PortalService[]): PortalRow[] =>
  [...services]
    .sort((a, b) => byName(a.service, b.service))
    .map((service) => ({
      key: String(service.connection_id),
      service,
      managers: service.delivered_managers
        .map((m) => ({ name: m.name, publicId: m.public_id }))
        .sort((a, b) => byName(a.name, b.name)),
      weekCount: service.recent_document_count,
      newest: service.newest_recent_document,
    }));

/** The Portals tab's heading (#15170): the distinct delivered managers and
 *  the portals listed. */
export const coverageHeading = (services: PortalService[]): string => {
  const managers = new Set(
    services.flatMap((s) => s.delivered_managers.map((m) => m.public_id))
  );
  return `Covering ${plural(managers.size, "manager")} across ${plural(
    services.length,
    "portal"
  )}`;
};

/** The muted line beside the heading (match-mockup spec section 1). */
export const coverageSubtitle = (services: PortalService[]): string => {
  const week = oncePerName(services).reduce(
    (sum, s) => sum + s.recent_document_count,
    0
  );
  return `Checked every business day · ${week.toLocaleString()} ${
    week === 1 ? "document" : "documents"
  } in the last 7 days`;
};

/** The Documents tab's count: every document the listed portals brought
 *  in. */
export const portalDocumentTotal = (services: PortalService[]): number =>
  oncePerName(services).reduce((sum, s) => sum + s.document_count, 0);

/** One service per name. Two connections listed under one name share their
 *  uploads (the upload string names the service, not the connection), so
 *  the read gives both the same counts; a total adds them once. */
const oncePerName = (services: PortalService[]): PortalService[] => {
  const seen = new Set<string>();
  return services.filter((s) => {
    if (seen.has(s.service)) return false;
    seen.add(s.service);
    return true;
  });
};

/** "Today 7:20 AM", "Yesterday 6:58 AM", else the month and day, in the
 *  viewer's zone (match-mockup spec section 1, Last checked). */
export const checkedLabel = (
  value: Temporal | null,
  zone: string,
  now: Date
): string => {
  if (value === null) return "—";
  switch (value.granularity) {
    case "date":
      return value.day;
    case "instant": {
      const day = formatInstant(value.at, zone);
      const today = todayInZone(zone, now);
      const time = formatInstantTime(value.at, zone);
      if (day === today) return `Today ${time}`;
      if (day === previousCalendarDay(today)) return `Yesterday ${time}`;
      return formatInstant(value.at, zone, "monthDay");
    }
    default: {
      const unhandled: never = value;
      throw new Error(
        `unhandled temporal granularity: ${JSON.stringify(unhandled)}`
      );
    }
  }
};

/** The day a document arrived, as the portal pages group by it. */
export type ArrivalBucket = "today" | "yesterday" | "week" | "older";

export const ARRIVAL_LABEL: Record<ArrivalBucket, string> = {
  today: "Today",
  yesterday: "Yesterday",
  week: "Earlier this week",
  older: "Older",
};

/** Which day group a document's arrival falls in, in the viewer's zone:
 *  today, yesterday, the rest of the last 7 days, or older. */
export const arrivalBucketOf = (
  uploadedAt: string | null,
  zone: string,
  now: Date
): ArrivalBucket => {
  if (uploadedAt === null) return "older";
  const day = formatInstant(uploadedAt, zone);
  const today = todayInZone(zone, now);
  if (day === today) return "today";
  if (day === previousCalendarDay(today)) return "yesterday";
  const since = calendarDaysBetween(day, today);
  return since !== null && since < 7 ? "week" : "older";
};

/** A newest document's day, after its name: "Today", "Yesterday", or the
 *  month and day. */
export const arrivalDayLabel = (
  uploadedAt: string,
  zone: string,
  now: Date
): string => {
  const bucket = arrivalBucketOf(uploadedAt, zone, now);
  return bucket === "today" || bucket === "yesterday"
    ? ARRIVAL_LABEL[bucket]
    : formatInstant(uploadedAt, zone, "monthDay");
};

/** The rows under their arrival day groups, newest first, as the portal
 *  pages' tables draw them. */
export const arrivalGroups = (
  rows: DocumentRow[],
  zone: string,
  now: Date
): { bucket: ArrivalBucket; label: string; rows: DocumentRow[] }[] => {
  const groups: {
    bucket: ArrivalBucket;
    label: string;
    rows: DocumentRow[];
  }[] = [];
  for (const row of rows) {
    const bucket = arrivalBucketOf(row.uploadedAt, zone, now);
    const last = groups[groups.length - 1];
    if (last && last.bucket === bucket) last.rows.push(row);
    else groups.push({ bucket, label: ARRIVAL_LABEL[bucket], rows: [row] });
  }
  return groups;
};
